/**
 * Confirmation Context
 * Contexto centralizado para exibir modais de confirmação
 */

import React, { createContext, useContext, useState, ReactNode } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from '@mui/material';
import { logger } from '@/utils/logger';

interface ConfirmationOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
}

interface ConfirmationContextType {
  confirm: (options: ConfirmationOptions) => void;
}

const ConfirmationContext = createContext<ConfirmationContextType | undefined>(undefined);

export const ConfirmationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmationOptions | null>(null);
  const [loading, setLoading] = useState(false);

  const confirm = (newOptions: ConfirmationOptions) => {
    setOptions(newOptions);
    setOpen(true);
  };

  const handleConfirm = async () => {
    if (!options) return;

    setLoading(true);
    try {
      await options.onConfirm();
      setOpen(false);
      setOptions(null);
    } catch (error) {
      logger.error('Error in confirmation action:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (options?.onCancel) {
      options.onCancel();
    }
    setOpen(false);
    setOptions(null);
  };

  return (
    <ConfirmationContext.Provider value={{ confirm }}>
      {children}
      <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
        <DialogTitle>{options?.title || 'Confirmar ação'}</DialogTitle>
        <DialogContent>
          <DialogContentText>{options?.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={loading} variant="outlined">
            {options?.cancelText || 'Cancelar'}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={loading}
            variant="contained"
            color={options?.confirmColor || 'primary'}
            autoFocus
          >
            {loading ? 'Processando...' : options?.confirmText || 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmationContext.Provider>
  );
};

export const useConfirmation = () => {
  const context = useContext(ConfirmationContext);
  if (!context) {
    throw new Error('useConfirmation must be used within a ConfirmationProvider');
  }
  return context;
};
